import { Switch, Route, Redirect, useLocation } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { ErrorBoundary } from "@/components/error-boundary";
import { HideDevTools } from "@/components/hide-dev-tools";
import { lazy, Suspense, useState, useEffect } from "react";
import { RoleBasedLayout } from "./components/layout/role-based-layout";
import { AuthProvider, useAuth } from "@/hooks/use-auth";
import { useSelectedPointsStore } from "@/store/selected-points-store";
import { Loader2 } from "lucide-react";
import { detectBrand, getDomainBrand } from "@/lib/brand-config";
import { isRoadMoverInvestor } from "@shared/roadmover-investor";
import { useWorkspaceBrand } from "@/hooks/use-workspace-brand";
import { getAccountBrand } from "@shared/branding";

export { useSelectedPointsStore };

// ─── Lazy-loaded pages (only downloaded when first visited) ─────────────────
const LeafletMap          = lazy(() => import("@/components/leaflet-map").then(m => ({ default: m.LeafletMap })));
const MapVisualizer       = lazy(() => import("@/components/map-visualizer").then(m => ({ default: m.MapVisualizer })));
const WorkingCommPanel    = lazy(() => import("./components/working-communication-panel").then(m => ({ default: m.WorkingCommunicationPanel })));
const DashboardPage       = lazy(() => import("./pages/dashboard"));

const GodAdminAccountView = lazy(() => import("./pages/dashboard/godadmin-account-view"));
const SuperAdminMgmt      = lazy(() => import("./pages/dashboard/super-admin-management"));
const SuperAdminMgmtNew   = lazy(() => import("./pages/dashboard/super-admin-management-new"));
const SuperAdminDetail    = lazy(() => import("./pages/dashboard/super-admin-detail"));
const AdminList           = lazy(() => import("./pages/dashboard/admin-list"));
const AdminManagement     = lazy(() => import("./pages/dashboard/admin-management"));
const DealnetDashboard    = lazy(() => import("./pages/dashboard/dealnet-superadmin-dashboard"));
const MapView             = lazy(() => import("./pages/map-view"));
const AuthPageNew         = lazy(() => import("./pages/auth-page-new"));
const DealNetLogin        = lazy(() => import("./pages/brand-login").then(m => ({ default: m.DealNetLogin })));
const LiquorNetLogin      = lazy(() => import("./pages/brand-login").then(m => ({ default: m.LiquorNetLogin })));
const GoFishingNetLogin   = lazy(() => import("./pages/brand-login").then(m => ({ default: m.GoFishingNetLogin })));
const RoadMoverLogin      = lazy(() => import("./pages/roadmover-login").then(m => ({ default: m.RoadMoverLogin })));
const RoadMoverInvestor   = lazy(() => import("./pages/roadmover-investor"));
const CreditsPage         = lazy(() => import("./pages/dashboard/credits"));
const DebugPage           = lazy(() => import("./pages/debug"));
const LandingPage         = lazy(() => import("./pages/landing-page"));
const LeadsPage           = lazy(() => import("./pages/leads"));
const HotLeadsPage        = lazy(() => import("./pages/hot-leads"));
const DataAxlePreview     = lazy(() => import("./components/dataaxle-preview").then(m => ({ default: m.DataAxlePreview })));
const NotFound            = lazy(() => import("@/pages/not-found"));
const OnboardPage         = lazy(() => import("./pages/onboard"));

// ─── Shared loading fallback ────────────────────────────────────────────────
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}

// ─── Using interface instead of import to avoid DataPoint type issues ────────
interface DataPoint {
  id: string;
  lat: number;
  lng: number;
  phone_number?: string;
  first_name?: string;
  last_name?: string;
  company?: string;
  selected: boolean;
}

// ─── GlobalDialer — renders the persistent softphone panel ──────────────────
// Only show on authenticated app routes — not on the landing page or login screens
const PUBLIC_PATHS = ['/', '/auth', '/debug', '/onboard'];

function GlobalDialer() {
  const [location] = useLocation();
  const selectedPoints = useSelectedPointsStore(state => state.selectedPoints);
  const { user, isLoading } = useAuth();

  const isPublicPath = PUBLIC_PATHS.includes(location) || location.startsWith('/login/');
  if (isLoading || !user || isPublicPath || location === "/roadmover/investor" || isRoadMoverInvestor(user)) return null;

  return (
    <Suspense fallback={null}>
      <WorkingCommPanel selectedPoints={selectedPoints} />
    </Suspense>
  );
}

// ─── Protected route wrapper ─────────────────────────────────────────────────
function ProtectedRoute({ children, path }: { children: React.ReactNode; path: string }) {
  const { user, isLoading } = useAuth();

  if (isLoading) return <PageLoader />;
  if (!user) return <Redirect to="/auth" />;

  return <Route path={path}>{children}</Route>;
}

// ─── Router ──────────────────────────────────────────────────────────────────
function Router() {
  const [selectedPoints, setSelectedPoints] = useState<DataPoint[]>([]);
  const { user, isLoading } = useAuth();
  const [location] = useLocation();

  if (user && isRoadMoverInvestor(user) && location !== "/roadmover/investor" && location !== "/login/roadmover") {
    return <Redirect to="/roadmover/investor" />;
  }

  // RoadMover's public entry points must never open another brand's login,
  // including bookmarked /auth URLs and explicit /login/<other-brand> links.
  const roadMoverDomain = getDomainBrand(window.location.hostname) === "roadmover";
  if (roadMoverDomain && (
    location === "/" || location === "/auth" ||
    (location.startsWith("/login/") && location !== "/login/roadmover")
  )) {
    return <Redirect to="/login/roadmover" />;
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Switch>
        <Route path="/roadmover/investor">
          {isLoading ? <PageLoader /> : !user ? <Redirect to="/login/roadmover" /> :
            isRoadMoverInvestor(user) || user.role === "godadmin" ? <RoadMoverInvestor /> :
              <div className="min-h-screen flex items-center justify-center"><p>This presentation requires investor access. <a href="/app" className="underline">Return to your workspace</a></p></div>}
        </Route>
        {/* Brand-specific preview routes — work without a custom domain */}
        <Route path="/login/dealnet">
          <DealNetLogin />
        </Route>
        <Route path="/login/liquornet">
          <LiquorNetLogin />
        </Route>
        <Route path="/login/gofishingnet">
          <GoFishingNetLogin />
        </Route>
        <Route path="/login/roadmover">
          <RoadMoverLogin />
        </Route>

        <Route path="/auth">
          <AuthPageNew />
        </Route>

        <Route path="/">
          {() => {
            const brand = detectBrand();
            if (brand === 'dealnet')      return <Redirect to="/login/dealnet" />;
            if (brand === 'liquornet')    return <Redirect to="/login/liquornet" />;
            if (brand === 'gofishingnet') return <Redirect to="/login/gofishingnet" />;
            if (brand === 'roadmover')    return <Redirect to="/login/roadmover" />;
            return <LandingPage />;
          }}
        </Route>

        <ProtectedRoute path="/app">
          <RoleBasedLayout pageTitle="Gewgul - Power Dialer">
            <div className="container mx-auto p-4">
              <ErrorBoundary>
                <LeafletMap onPointsSelected={setSelectedPoints} />
              </ErrorBoundary>
            </div>
          </RoleBasedLayout>
        </ProtectedRoute>

        <ProtectedRoute path="/dashboard/accounts/:accountId">
          <RoleBasedLayout pageTitle="GodAdmin - Account Activity">
            <GodAdminAccountView />
          </RoleBasedLayout>
        </ProtectedRoute>

        <ProtectedRoute path="/dashboard">
          <RoleBasedLayout pageTitle="Brock - Dashboard">
            <DashboardPage />
          </RoleBasedLayout>
        </ProtectedRoute>

        <ProtectedRoute path="/dialer">
          <RoleBasedLayout pageTitle="Brock - Power Dialer">
            <div className="container mx-auto p-4">
              <ErrorBoundary>
                <h1 className="text-2xl font-bold mb-6">Power Dialer</h1>
                <LeafletMap onPointsSelected={setSelectedPoints} />
              </ErrorBoundary>
            </div>
          </RoleBasedLayout>
        </ProtectedRoute>

        <ProtectedRoute path="/map">
          <RoleBasedLayout pageTitle="Brock - Map View">
            <div className="container mx-auto p-4">
              <ErrorBoundary>
                <h1 className="text-2xl font-bold mb-6">Advanced Map Visualization</h1>
                <MapVisualizer onPointsSelected={setSelectedPoints} />
              </ErrorBoundary>
            </div>
          </RoleBasedLayout>
        </ProtectedRoute>

        <ProtectedRoute path="/map-view">
          <RoleBasedLayout pageTitle="Gewgul - Map View">
            <MapView />
          </RoleBasedLayout>
        </ProtectedRoute>

        <ProtectedRoute path="/system">
          <RoleBasedLayout pageTitle="Unicorn Joystick - System Overview">
            <div className="container mx-auto p-4">
              <h1 className="text-2xl font-bold">System Overview</h1>
              <p className="text-muted-foreground">Global system configuration and monitoring</p>
            </div>
          </RoleBasedLayout>
        </ProtectedRoute>

        <ProtectedRoute path="/companies">
          <RoleBasedLayout pageTitle="Unicorn Joystick - Companies">
            <div className="container mx-auto p-4">
              <h1 className="text-2xl font-bold">Companies Management</h1>
              <p className="text-muted-foreground">Manage all companies in the system</p>
            </div>
          </RoleBasedLayout>
        </ProtectedRoute>

        <ProtectedRoute path="/dashboard/super-admin-management">
          <RoleBasedLayout pageTitle="Unicorn Joystick - Super Admin Management">
            <SuperAdminMgmt />
          </RoleBasedLayout>
        </ProtectedRoute>

        <ProtectedRoute path="/dashboard/super-admin/:id/admins">
          <RoleBasedLayout pageTitle="Unicorn Joystick - Admin Management">
            <ErrorBoundary>
              <AdminList />
            </ErrorBoundary>
          </RoleBasedLayout>
        </ProtectedRoute>

        <ProtectedRoute path="/dashboard/super-admin/:id">
          <RoleBasedLayout pageTitle="Unicorn Joystick - Super Admin Details">
            <ErrorBoundary>
              <SuperAdminDetail />
            </ErrorBoundary>
          </RoleBasedLayout>
        </ProtectedRoute>

        <ProtectedRoute path="/super-admins">
          <RoleBasedLayout pageTitle="Unicorn Joystick - Super Admins">
            <ErrorBoundary>
              <SuperAdminMgmtNew />
            </ErrorBoundary>
          </RoleBasedLayout>
        </ProtectedRoute>

        <ProtectedRoute path="/company">
          <RoleBasedLayout pageTitle="Unicorn Joystick - Company Dashboard">
            <div className="container mx-auto p-4">
              <h1 className="text-2xl font-bold">Company Dashboard</h1>
              <p className="text-muted-foreground">Company-level management</p>
            </div>
          </RoleBasedLayout>
        </ProtectedRoute>

        <ProtectedRoute path="/dealnet-dashboard">
          <RoleBasedLayout pageTitle="DealNet - Team Dashboard">
            <div className="container mx-auto p-4">
              <DealnetDashboard />
            </div>
          </RoleBasedLayout>
        </ProtectedRoute>

        <ProtectedRoute path="/admins">
          <RoleBasedLayout pageTitle="Unicorn Joystick - Administrators">
            <AdminManagement />
          </RoleBasedLayout>
        </ProtectedRoute>

        <ProtectedRoute path="/users">
          <RoleBasedLayout pageTitle="Unicorn Joystick - Users">
            <div className="container mx-auto p-4">
              <h1 className="text-2xl font-bold">Users</h1>
              <p className="text-muted-foreground">Manage user accounts</p>
            </div>
          </RoleBasedLayout>
        </ProtectedRoute>

        <ProtectedRoute path="/queues">
          <RoleBasedLayout pageTitle="Unicorn Joystick - Dialer Queues">
            <div className="container mx-auto p-4">
              <h1 className="text-2xl font-bold">Dialer Queues</h1>
              <p className="text-muted-foreground">Manage dialing queues</p>
            </div>
          </RoleBasedLayout>
        </ProtectedRoute>

        <ProtectedRoute path="/dashboard/credits">
          <RoleBasedLayout pageTitle="Unicorn Joystick - Credits">
            <CreditsPage />
          </RoleBasedLayout>
        </ProtectedRoute>

        <ProtectedRoute path="/settings">
          <RoleBasedLayout pageTitle="Unicorn Joystick - Settings">
            <div className="container mx-auto p-4">
              <h1 className="text-2xl font-bold">Settings</h1>
              <p className="text-muted-foreground">Configure your account settings</p>
            </div>
          </RoleBasedLayout>
        </ProtectedRoute>

        <ProtectedRoute path="/dataaxle">
          <RoleBasedLayout pageTitle="Gewgul - Magical Search">
            <DataAxlePreview />
          </RoleBasedLayout>
        </ProtectedRoute>

        <ProtectedRoute path="/leads">
          <RoleBasedLayout pageTitle="Gewgul - Leads">
            <LeadsPage />
          </RoleBasedLayout>
        </ProtectedRoute>

        <ProtectedRoute path="/hot-leads">
          <RoleBasedLayout pageTitle="Gewgul - Hot Leads">
            <HotLeadsPage />
          </RoleBasedLayout>
        </ProtectedRoute>

        <Route path="/debug">
          <DebugPage />
        </Route>

        <Route path="/onboard">
          <OnboardPage />
        </Route>

        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

// ─── Idle auto-logout ────────────────────────────────────────────────────────
const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

function useIdleLogout() {
  const { user, logoutMutation } = useAuth();
  useEffect(() => {
    if (!user) return; // only run when logged in
    let timer: ReturnType<typeof setTimeout>;

    const logout = async () => {
      logoutMutation.mutate();
    };

    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(logout, IDLE_TIMEOUT_MS);
    };

    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
    events.forEach(e => window.addEventListener(e, reset, { passive: true }));
    reset(); // start the timer on mount

    return () => {
      clearTimeout(timer);
      events.forEach(e => window.removeEventListener(e, reset));
    };
  }, [user, logoutMutation.mutate]);
}

// ─── Idle logout guard — must live inside AuthProvider ───────────────────────
function IdleLogoutGuard() {
  useIdleLogout();
  return null;
}

function WorkspaceDocumentTitle() {
  const { user } = useAuth();
  const workspaceBrand = useWorkspaceBrand(user as any);
  const roadmover = user ? getAccountBrand(user) === 'roadmover' : workspaceBrand.roadmover;
  useEffect(() => {
    if (roadmover) document.body.setAttribute("data-brand", "roadmover");
    else if (document.body.getAttribute("data-brand") === "roadmover") document.body.removeAttribute("data-brand");
    return () => {
      if (document.body.getAttribute("data-brand") === "roadmover") document.body.removeAttribute("data-brand");
    };
  }, [roadmover]);
  useEffect(() => {
    if (!roadmover) {
      const brand = user ? getAccountBrand(user) : detectBrand();
      const defaultTitles: Record<string, string> = {
        dealnet: "DealNet - Outreach Platform",
        liquornet: "LiquorNet - Outreach Platform",
        gofishingnet: "GoFishingNet - Outreach Platform",
        gewgul: "Gewgul - AI-Powered Data Targeting & Outreach Platform",
      };
      document.title = defaultTitles[brand] || defaultTitles.gewgul;
      return;
    }
    document.title = "RoadMover — Moving & Storage Operations";
    const icon = document.querySelector<HTMLLinkElement>('link[rel~="icon"]');
    if (!icon) return;
    const previousHref = icon.href;
    icon.href = `data:image/svg+xml,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><rect width="64" height="64" rx="14" fill="#10243b"/><path d="M8 18h31v24H8zM39 26h10l8 9v7H39z" fill="none" stroke="#e7a83e" stroke-width="4" stroke-linejoin="round"/><circle cx="20" cy="45" r="5" fill="#10243b" stroke="#e7a83e" stroke-width="4"/><circle cx="48" cy="45" r="5" fill="#10243b" stroke="#e7a83e" stroke-width="4"/></svg>')}`;
    return () => { icon.href = previousHref; };
  }, [roadmover, user]);
  return null;
}

// ─── App root ────────────────────────────────────────────────────────────────
function App() {
  useEffect(() => {
    if (navigator.mediaDevices?.getUserMedia) {
      navigator.mediaDevices.getUserMedia({ audio: true })
        .then(stream => stream.getTracks().forEach(t => t.stop()))
        .catch(() => {});
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <IdleLogoutGuard />
        <WorkspaceDocumentTitle />
        <HideDevTools />
        <div className="tron-grid-overlay" />
        <ErrorBoundary>
          <GlobalDialer />
        </ErrorBoundary>
        <Router />
        <Toaster />
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
